import React, { useState } from 'react';
import { Container, Stack, Input, Textarea, Button, Alert } from '@/components/ui';
import { PageHeader } from '@/components/ui/PageHeader/PageHeader';
import { useNotificationStore } from '../../stores/notificationStore';
import { useOrgStore } from '../../stores/orgStore';
import { OrgKnowledgeService } from '@/services/OrgKnowledgeService';
import { useOrgKnowledgeProfile, useSaveOrgKnowledgeProfileMutation } from '@/hooks/queries';

export function AboutCompany() {
  const svc = new OrgKnowledgeService();
  const activeOrg = useOrgStore(s => s.orgs.find(org => org.id === s.activeOrgId));
  const { data: rawProfile, isLoading: loading } = useOrgKnowledgeProfile();
  const saveProfileMutation = useSaveOrgKnowledgeProfileMutation();
  const [error, setError] = useState<string | null>(null);
  const [websiteError, setWebsiteError] = useState<string | null>(null);
  const [suggesting, setSuggesting] = useState(false);
  const [profile, setProfile] = useState({
    website_url: '',
    mission: '',
    vision: '',
    value_proposition: '',
    tone: '',
    brand_voice: '',
    target_audience: '',
    products_and_services: '',
  });

  // Sync profile state with query data
  React.useEffect(() => {
    if (rawProfile) {
      setProfile({
        website_url: rawProfile.website_url || '',
        mission: rawProfile.mission || '',
        vision: rawProfile.vision || '',
        value_proposition: rawProfile.value_proposition || '',
        tone: rawProfile.tone || '',
        brand_voice: rawProfile.brand_voice || '',
        target_audience: rawProfile.target_audience || '',
        products_and_services: rawProfile.products_and_services || '',
      });
    }
  }, [rawProfile]);

  const isValidWebsiteUrl = (value: string): boolean => {
    if (!value) return true; // optional
    const trimmed = value.trim();
    if (trimmed.length === 0) return true;
    if (/\s/.test(trimmed)) return false;
    try {
      const url = new URL(trimmed);
      if (url.protocol !== 'http:' && url.protocol !== 'https:') return false;
      return true;
    } catch {
      return false;
    }
  };

  const handleSave = async () => {
    try {
      setError(null);
      // Validate website before save
      const validWebsite = isValidWebsiteUrl(profile.website_url);
      if (!validWebsite) {
        setWebsiteError('Enter a valid URL starting with http:// or https:// and no spaces');
        return;
      }
      setWebsiteError(null);
      await saveProfileMutation.mutateAsync(profile);
      try { useNotificationStore.getState().success('Profile saved successfully'); } catch {}
    } catch (e: any) {
      setError(e?.message || 'Failed to save');
    }
  };

  return (
    <div className="p-8">
        <PageHeader
          title="Organization"
          description="Provide core information to help AI understand your brand."
        />

        {error && (
          <div className="mb-4">
            <Alert variant="error" title="Error">{error}</Alert>
          </div>
        )}

        <Stack spacing={6}>
          <Input
            label="Organization Name"
            placeholder="Organization name"
            value={activeOrg?.name || ''}
            disabled
            hint="Managed at organization creation"
          />
          <Input
            label="Website URL"
            placeholder="https://example.com"
            type="url"
            value={profile.website_url}
            onChange={(e) => {
              const v = e.target.value;
              setProfile(p => ({ ...p, website_url: v }));
              setWebsiteError(isValidWebsiteUrl(v) ? null : 'Enter a valid URL starting with http:// or https:// and no spaces');
            }}
            onBlur={() => setWebsiteError(isValidWebsiteUrl(profile.website_url) ? null : 'Enter a valid URL starting with http:// or https:// and no spaces')}
            error={websiteError || undefined}
            disabled={loading}
          />
          <Textarea
            label="Mission"
            placeholder="Why does your company exist?"
            value={profile.mission}
            onChange={(e) => setProfile(p => ({ ...p, mission: e.target.value }))}
            rows={4}
            disabled={loading}
          />
          <Textarea
            label="Vision"
            placeholder="What future are you building?"
            value={profile.vision}
            onChange={(e) => setProfile(p => ({ ...p, vision: e.target.value }))}
            rows={4}
            disabled={loading}
          />
          <Textarea
            label="Value Proposition"
            placeholder="What unique value do you deliver to customers?"
            value={profile.value_proposition}
            onChange={(e) => setProfile(p => ({ ...p, value_proposition: e.target.value }))}
            rows={4}
            disabled={loading}
          />
          <Input
            label="Tone (e.g., warm, professional, playful)"
            placeholder="Describe your brand tone"
            value={profile.tone}
            onChange={(e) => setProfile(p => ({ ...p, tone: e.target.value }))}
            disabled={loading}
          />
          <Textarea
            label="Brand Voice Guidelines"
            placeholder="Short instructions on phrasing, style, taboo topics, and must-use phrases."
            value={profile.brand_voice}
            onChange={(e) => setProfile(p => ({ ...p, brand_voice: e.target.value }))}
            rows={5}
            disabled={loading}
          />
          <Textarea
            label="Products & Services"
            placeholder="Describe your products and services."
            value={profile.products_and_services}
            onChange={(e) => setProfile(p => ({ ...p, products_and_services: e.target.value }))}
            rows={5}
            disabled={loading}
          />
          <Textarea
            label="Target Audience"
            placeholder="Who do you sell to?"
            value={profile.target_audience}
            onChange={(e) => setProfile(p => ({ ...p, target_audience: e.target.value }))}
            rows={3}
            disabled={loading}
          />

          {/* Objectives removed in Milestone 4 */}
          <div className="flex justify-end">
            <div className="flex items-center gap-2">
              <Button variant="secondary" onClick={async () => {
                try {
                  setSuggesting(true);
                  setError(null);
                  const data = await svc.suggestProfileFromWebsite(profile.website_url || undefined);
                  setProfile(p => ({
                    ...p,
                    mission: data.mission || p.mission,
                    vision: data.vision || p.vision,
                    value_proposition: data.value_proposition || p.value_proposition,
                    tone: data.tone || p.tone,
                    brand_voice: data.brand_voice || p.brand_voice,
                    products_and_services: data.products_and_services || p.products_and_services,
                    target_audience: data.target_audience || p.target_audience,
                  }));
                  try { useNotificationStore.getState().info?.('Draft profile suggestions loaded'); } catch {}
                } catch (e: any) {
                  setError(e?.message || 'Failed to generate suggestions');
                } finally {
                  setSuggesting(false);
                }
              }} loading={suggesting} disabled={loading || suggesting || Boolean(websiteError)}>
                Generate from Website
              </Button>
              <Button variant="outline" onClick={async () => {
                try {
                  setSuggesting(true);
                  setError(null);
                  if (profile.website_url) {
                    await svc.validateWebsite(profile.website_url);
                  }
                  await svc.beginProfileResearch(profile.website_url || undefined);
                  try { useNotificationStore.getState().info?.('Background research started'); } catch {}
                } catch (e: any) {
                  setError(e?.message || 'Failed to start background research');
                } finally {
                  setSuggesting(false);
                }
              }} disabled={loading || suggesting || Boolean(websiteError)}>
                Start Background Research
              </Button>
              <Button variant="primary" onClick={handleSave} loading={saveProfileMutation.isPending} disabled={loading || Boolean(websiteError)}>
                Save Profile
              </Button>
            </div>
          </div>
        </Stack>
    </div>
  );
}
